/* LabChat — per-case multi-party clinical thread between the doctor's
 * clinic and the Invisique COE (planner / QA / production / admin).
 *
 * Two-pane layout (mirrors PatientChat exactly):
 *   Left  — Case inbox: cases with chat activity, sorted by most recent
 *   Right — Thread: messages oldest→newest + composer at the bottom
 *
 * URL: /doctor/lab-chat?case=<caseId>
 *   - Deep-linkable; refresh restores the same thread
 *   - Cross-linked from Case Detail via an "Open in Lab Chat" link
 *
 * Auto-refresh: lightweight 30-second polling while the page is open and
 * the tab is focused. We re-poll faster (5s) for 30 seconds after the
 * doctor sends a message so the reply lands quickly.
 *
 * COE names: planner / QA / production staff appear to the doctor as
 * "Center of Excellence — Planning" (etc.) — backend redaction is the
 * single source of truth; frontend just renders what it gets back.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
    ChatCircleDots, PaperPlaneTilt, MagnifyingGlass, ArrowRight, Spinner, ArrowsClockwise,
    Paperclip, X, FileText, Image as ImageIcon, Check, Checks,
    ArrowSquareOut, DotsThreeVertical, CaretRight, FileMagnifyingGlass,
} from "@phosphor-icons/react";


// Deterministic tint for the initials avatar — keeps the same patient
// the same color across mounts so the doctor builds a visual memory.
// (Same palette as PatientChat for cross-page consistency.)
const AVATAR_TINTS = [
    { bg: "bg-primary/15", border: "border-primary/30", text: "text-primary" },
    { bg: "bg-indigo-50",  border: "border-indigo-200", text: "text-indigo-700" },
    { bg: "bg-amber-50",   border: "border-amber-200", text: "text-amber-800" },
    { bg: "bg-rose-50",    border: "border-rose-200", text: "text-rose-700" },
    { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700" },
    { bg: "bg-sky-50",     border: "border-sky-200", text: "text-sky-700" },
];

function avatarTintFor(name) {
    const s = String(name || "?");
    let h = 0;
    for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) | 0;
    return AVATAR_TINTS[Math.abs(h) % AVATAR_TINTS.length];
}

const initialsOf = (name) =>
    String(name || "?")
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((s) => s[0].toUpperCase())
        .join("") || "?";


const POLL_MS = 30_000;        // baseline polling cadence
const FAST_POLL_MS = 5_000;    // briefly faster after the doctor sends


/* Inline timestamp formatter — produces "11:42" for today, "Tue 14:08"
   for this week, and full "Mar 12, 14:08" for older messages. */
function fmtTime(iso) {
    if (!iso) return "";
    const t = new Date(iso);
    if (Number.isNaN(t.getTime())) return "";
    const now = new Date();
    const sameDay = t.toDateString() === now.toDateString();
    if (sameDay) {
        return t.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
    }
    const within7d = now.getTime() - t.getTime() < 7 * 86400000;
    if (within7d) {
        return t.toLocaleString([], { weekday: "short", hour: "2-digit", minute: "2-digit", hour12: false });
    }
    return t.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", hour12: false });
}


function fmtSize(bytes) {
    if (!bytes || Number.isNaN(bytes)) return "—";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}


export default function LabChat() {
    const [searchParams, setSearchParams] = useSearchParams();
    const activeCaseId = searchParams.get("case") || null;

    const [inbox, setInbox] = useState([]);
    const [inboxLoading, setInboxLoading] = useState(true);
    const [inboxQ, setInboxQ] = useState("");
    const [thread, setThread] = useState([]);
    const [threadLoading, setThreadLoading] = useState(false);
    const [draft, setDraft] = useState("");
    // `pendingAttachments` are files the doctor picked but hasn't sent yet
    // — they're already uploaded (so we have an id + url for preview) but
    // not yet associated with any message. Sending the message flushes
    // them as `attachment_file_ids` and clears this tray.
    const [pendingAttachments, setPendingAttachments] = useState([]);
    const [uploadingCount, setUploadingCount] = useState(0);
    const [sending, setSending] = useState(false);
    const scrollRef = useRef(null);
    const pollTimerRef = useRef(null);
    const fileInputRef = useRef(null);

    /* Load inbox once on mount. Subsequent activity (sending a message)
       will fold its new last_message_at into the inbox via reloadInbox. */
    const reloadInbox = useCallback(async () => {
        try {
            const { data } = await api.get("/doctor/lab-chat/inbox");
            setInbox(data || []);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not load Lab Chat inbox");
        } finally {
            setInboxLoading(false);
        }
    }, []);

    useEffect(() => { reloadInbox(); }, [reloadInbox]);

    /* Thread loader — runs whenever the active case changes. Also called
       from the polling effect below to refresh silently in the background.
       After a successful (non-silent) load we fire mark-read so the inbox
       unread badge clears as soon as the doctor opens the thread. */
    const reloadThread = useCallback(async (caseId, opts = {}) => {
        if (!caseId) { setThread([]); return; }
        if (!opts.silent) setThreadLoading(true);
        try {
            const { data } = await api.get(`/cases/${caseId}/messages`);
            setThread(data || []);
            if (!opts.skipMarkRead) {
                // Don't block UI on mark-read; fire and forget. If it
                // succeeds, refresh inbox so the unread badge clears.
                api.post(`/cases/${caseId}/messages/mark-read`)
                    .then((r) => { if (r.data?.marked > 0) reloadInbox(); })
                    .catch(() => { /* mark-read failures are non-critical */ });
            }
        } catch (err) {
            if (!opts.silent) toast.error(err.response?.data?.detail || "Could not load thread");
        } finally {
            if (!opts.silent) setThreadLoading(false);
        }
    }, [reloadInbox]);

    useEffect(() => {
        // Switching threads — clear any stale pending attachments so we
        // don't accidentally send Case A's photos into Case B's thread.
        setPendingAttachments([]);
        setDraft("");
        reloadThread(activeCaseId);
    }, [activeCaseId, reloadThread]);

    /* Polling — re-pulls the active thread every POLL_MS while the page
       has focus. We deliberately skip polling while the doctor is typing
       (focus event from the composer pauses it) to avoid jitter. */
    useEffect(() => {
        if (!activeCaseId) return undefined;
        let cancelled = false;
        const tick = async () => {
            if (cancelled || document.hidden) return;
            // Silent + skipMarkRead — we already marked-as-read on the
            // initial load. The polling tick just refreshes content; we
            // don't want to spam mark-read or noisily refresh the inbox.
            await reloadThread(activeCaseId, { silent: true, skipMarkRead: true });
        };
        pollTimerRef.current = setInterval(tick, POLL_MS);
        return () => {
            cancelled = true;
            if (pollTimerRef.current) clearInterval(pollTimerRef.current);
        };
    }, [activeCaseId, reloadThread]);

    /* Auto-scroll to the latest message whenever the thread grows. */
    useEffect(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    }, [thread.length]);

    /* Upload picked files as chat_attachment, hold the responses in
       `pendingAttachments` until the message is sent. We upload as soon
       as the file is picked (instead of at send time) so the doctor sees
       the inline preview immediately and the send button just attaches
       refs. Slow uploads block the send button via `uploadingCount > 0`. */
    const onFilesPicked = useCallback(async (files) => {
        if (!activeCaseId) return;
        const list = Array.from(files || []);
        if (list.length === 0) return;
        for (const file of list) {
            setUploadingCount((n) => n + 1);
            try {
                const form = new FormData();
                form.append("file", file);
                const { data } = await api.post(
                    `/cases/${activeCaseId}/upload?kind=chat_attachment`,
                    form,
                    { headers: { "Content-Type": "multipart/form-data" } },
                );
                setPendingAttachments((arr) => [...arr, {
                    id: data.id,
                    name: data.name,
                    content_type: data.content_type,
                    size: data.size,
                    url: `/api/files/${data.id}`,
                    is_image: (data.content_type || "").startsWith("image/"),
                }]);
            } catch (err) {
                toast.error(err.response?.data?.detail || `Couldn't upload ${file.name}`);
            } finally {
                setUploadingCount((n) => n - 1);
            }
        }
        // Reset the input so the doctor can re-pick the same file later if
        // needed (browsers ignore re-picks of the same path otherwise).
        if (fileInputRef.current) fileInputRef.current.value = "";
    }, [activeCaseId]);

    const removePendingAttachment = (id) => {
        setPendingAttachments((arr) => arr.filter((a) => a.id !== id));
    };

    const sendMessage = async () => {
        const text = draft.trim();
        const attachmentIds = pendingAttachments.map((a) => a.id);
        // Bail when there's nothing to send. The composer's send button is
        // also disabled in this state, this is just a safety net.
        if ((!text && attachmentIds.length === 0) || sending || !activeCaseId) return;
        setSending(true);
        // Optimistic append so the doctor sees their bubble immediately.
        const optimisticId = `opt-${Date.now()}`;
        const optimistic = {
            id: optimisticId,
            author_name: "You",
            author_role: "doctor",
            text,
            attachments: pendingAttachments,    // already-uploaded refs
            read_by: [],
            created_at: new Date().toISOString(),
            _pending: true,
        };
        setThread((m) => [...m, optimistic]);
        setDraft("");
        setPendingAttachments([]);
        try {
            const { data } = await api.post(`/cases/${activeCaseId}/messages`, {
                text,
                attachment_file_ids: attachmentIds,
            });
            setThread((m) => m.map((x) => (x.id === optimisticId ? data : x)));
            reloadInbox();
            // Faster poll for the next 30 seconds to catch a quick reply.
            if (pollTimerRef.current) clearInterval(pollTimerRef.current);
            let bursts = 0;
            const fast = setInterval(() => {
                bursts += 1;
                if (bursts > 6) {
                    clearInterval(fast);
                    pollTimerRef.current = setInterval(
                        () => !document.hidden && reloadThread(activeCaseId, { silent: true, skipMarkRead: true }),
                        POLL_MS,
                    );
                    return;
                }
                if (!document.hidden) reloadThread(activeCaseId, { silent: true, skipMarkRead: true });
            }, FAST_POLL_MS);
            pollTimerRef.current = fast;
        } catch (err) {
            // Roll back optimistic state on failure — restore the draft +
            // attachments so the doctor doesn't lose their work.
            setThread((m) => m.filter((x) => x.id !== optimisticId));
            setDraft(text);
            setPendingAttachments(optimistic.attachments || []);
            toast.error(err.response?.data?.detail || "Couldn't send message");
        } finally {
            setSending(false);
        }
    };

    const filteredInbox = useMemo(() => {
        const q = inboxQ.trim().toLowerCase();
        if (!q) return inbox;
        return inbox.filter((c) =>
            (c.patient_name || "").toLowerCase().includes(q) ||
            (c.code || "").toLowerCase().includes(q) ||
            (c.last_message_preview || "").toLowerCase().includes(q)
        );
    }, [inbox, inboxQ]);

    const setActive = (caseId) => {
        const next = new URLSearchParams(searchParams);
        if (caseId) next.set("case", caseId); else next.delete("case");
        setSearchParams(next, { replace: true });
    };

    const activeCaseMeta = inbox.find((c) => c.id === activeCaseId);

    return (
        <div data-testid="doctor-lab-chat" className="space-y-4">
            {/* ── Page header ── matches /doctor/cases + /doctor/patients grammar */}
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div>
                    <h1 className="font-display text-2xl font-bold tracking-tight">Lab Chat</h1>
                    <p className="text-xs text-muted-foreground mt-1">
                        Per-case threads with the Invisique™ planning, QA and production teams — one conversation per case.
                    </p>
                </div>
            </div>
            <div className="h-[calc(100vh-13rem)] min-h-[500px] flex gap-4">
            {/* LEFT — case inbox */}
            <aside className="w-[300px] shrink-0 bg-card border border-border rounded-2xl flex flex-col overflow-hidden">
                <div className="p-3 border-b border-border space-y-2.5">
                    <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-1.5">
                            <ChatCircleDots size={15} weight="duotone" className="text-primary" />
                            <h2 className="font-display text-sm font-bold">Inbox</h2>
                        </div>
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-[11px]"
                            onClick={() => { reloadInbox(); if (activeCaseId) reloadThread(activeCaseId); }}
                            data-testid="lab-chat-refresh"
                        >
                            <ArrowsClockwise size={11} className="mr-1" /> Refresh
                        </Button>
                    </div>
                    <div className="relative">
                        <MagnifyingGlass size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <input
                            value={inboxQ}
                            onChange={(e) => setInboxQ(e.target.value)}
                            placeholder="Search patient / case…"
                            className="w-full h-8 pl-7 pr-2 text-xs rounded-md border border-border bg-background"
                            data-testid="lab-chat-inbox-search"
                        />
                    </div>
                </div>
                <div className="flex-1 overflow-y-auto" data-testid="lab-chat-inbox">
                    {inboxLoading ? (
                        <InboxSkeleton />
                    ) : filteredInbox.length === 0 ? (
                        <InboxEmpty hasFilter={inboxQ.length > 0} />
                    ) : (
                        filteredInbox.map((c) => (
                            <InboxRow
                                key={c.id}
                                row={c}
                                active={c.id === activeCaseId}
                                onClick={() => setActive(c.id)}
                            />
                        ))
                    )}
                </div>
            </aside>

            {/* RIGHT — thread */}
            <div className="flex-1 min-w-0 bg-card border border-border rounded-2xl flex flex-col overflow-hidden">
                {!activeCaseId ? (
                    <ThreadEmpty
                        hasInbox={inbox.length > 0}
                        recents={inbox.slice(0, 5)}
                        onPickCase={(cid) => setActive(cid)}
                    />
                ) : (
                    <>
                        <ThreadHeader meta={activeCaseMeta} caseId={activeCaseId} />
                        <div
                            ref={scrollRef}
                            className="flex-1 overflow-y-auto p-5 space-y-3"
                            data-testid="lab-chat-thread"
                        >
                            {threadLoading && thread.length === 0 ? (
                                <div className="text-center text-muted-foreground text-xs py-12">
                                    <Spinner size={20} className="mx-auto mb-2 animate-spin opacity-60" />
                                    Loading thread…
                                </div>
                            ) : thread.length === 0 ? (
                                <ThreadBlank />
                            ) : (
                                thread.map((m) => <MessageBubble key={m.id} msg={m} />)
                            )}
                        </div>
                        <Composer
                            draft={draft} setDraft={setDraft}
                            onSend={sendMessage}
                            sending={sending}
                            pendingAttachments={pendingAttachments}
                            onFilesPicked={onFilesPicked}
                            removePendingAttachment={removePendingAttachment}
                            uploadingCount={uploadingCount}
                            fileInputRef={fileInputRef}
                        />
                    </>
                )}
            </div>
        </div>
        </div>
    );
}


/* ─────────────────── Inbox subcomponents ─────────────────── */

function InboxSkeleton() {
    return (
        <div className="p-3 space-y-3">
            {[1, 2, 3].map((i) => (
                <div key={i} className="space-y-1.5 animate-pulse">
                    <div className="h-3 bg-muted rounded w-2/3" />
                    <div className="h-2 bg-muted rounded w-1/2" />
                </div>
            ))}
        </div>
    );
}


function InboxEmpty({ hasFilter }) {
    return (
        <div className="p-6 text-center">
            <ChatCircleDots size={26} weight="duotone" className="mx-auto mb-2 text-muted-foreground/60" />
            <p className="text-xs text-muted-foreground">
                {hasFilter ? "No matches in this view." : "No conversations yet."}
            </p>
            {!hasFilter && (
                <Link to="/doctor/cases" className="text-[11px] font-semibold text-primary hover:underline mt-2 inline-block">
                    Open a case to start a thread <ArrowRight size={10} className="inline" />
                </Link>
            )}
        </div>
    );
}


function InboxRow({ row, active, onClick }) {
    const unread = row.unread_count || 0;
    const tint = avatarTintFor(row.patient_name);
    return (
        <button
            type="button"
            onClick={onClick}
            className={`relative w-full text-left px-4 py-3 border-b border-border last:border-b-0 transition-colors ${
                active ? "bg-primary/10" : "hover:bg-secondary/50"
            }`}
            data-testid={`lab-chat-inbox-row-${row.id}`}
        >
            <div className="flex items-start gap-3">
                {row.patient_photo_data_url ? (
                    <img
                        src={row.patient_photo_data_url}
                        alt={row.patient_name || "Patient"}
                        className={`w-9 h-9 rounded-full object-cover border ${tint.border} shrink-0`}
                    />
                ) : (
                    <div className={`w-9 h-9 rounded-full grid place-items-center text-[11px] font-semibold border ${tint.bg} ${tint.border} ${tint.text} shrink-0`}>
                        {initialsOf(row.patient_name)}
                    </div>
                )}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                        <div className={`text-sm truncate ${unread > 0 ? "font-bold" : "font-semibold"} text-foreground`}>
                            {row.patient_name || "Patient"}
                        </div>
                        <div className="text-[10px] text-muted-foreground shrink-0 tabular-nums">
                            {fmtTime(row.last_message_at)}
                        </div>
                    </div>
                    <div className="flex items-center gap-1.5 mt-0.5 text-[10px] text-muted-foreground">
                        <span className="font-mono text-primary truncate">{row.code || "—"}</span>
                        {row.status && (
                            <>
                                <span>·</span>
                                <span className="capitalize truncate">{row.status.replace(/_/g, " ")}</span>
                            </>
                        )}
                    </div>
                    <div className={`text-[12px] mt-1 truncate ${
                        unread > 0 ? "text-foreground font-medium" : "text-muted-foreground"
                    }`}>
                        {row.last_message_preview || "—"}
                    </div>
                </div>
            </div>
            {unread > 0 && (
                <span
                    className="absolute top-4 right-4 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full bg-primary text-white text-[9px] font-bold tabular-nums"
                    data-testid={`lab-chat-unread-${row.id}`}
                >
                    {unread > 99 ? "99+" : unread}
                </span>
            )}
        </button>
    );
}


/* ─────────────────── Thread subcomponents ─────────────────── */

function ThreadHeader({ meta, caseId }) {
    const tint = avatarTintFor(meta?.patient_name);
    return (
        <div className="px-4 py-3 border-b border-border flex items-center gap-3" data-testid="lab-chat-thread-header">
            {/* Patient avatar — photo when available, initials fallback */}
            {meta?.patient_photo_data_url ? (
                <img
                    src={meta.patient_photo_data_url}
                    alt={meta.patient_name || "Patient"}
                    className={`w-9 h-9 rounded-full object-cover border ${tint.border} shrink-0`}
                />
            ) : (
                <div className={`w-9 h-9 rounded-full grid place-items-center text-[11px] font-semibold border shrink-0 ${tint.bg} ${tint.border} ${tint.text}`}>
                    {initialsOf(meta?.patient_name)}
                </div>
            )}
            <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm truncate">{meta?.patient_name || "Patient"}</div>
                <div className="text-[11px] text-muted-foreground flex items-center gap-1.5 mt-0.5">
                    <span className="font-mono text-primary">{meta?.code || "—"}</span>
                    {meta?.status && (
                        <>
                            <span>·</span>
                            <span className="capitalize">{meta.status.replace(/_/g, " ")}</span>
                        </>
                    )}
                </div>
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
                <Link
                    to={`/doctor/cases/${caseId}`}
                    className="w-8 h-8 rounded-md border border-border bg-card hover:bg-secondary/60 grid place-items-center text-muted-foreground transition-colors"
                    title="Open full case"
                    data-testid="lab-chat-open-case"
                >
                    <ArrowSquareOut size={14} />
                </Link>
                <Link
                    to={`/doctor/cases/${caseId}#files`}
                    className="w-8 h-8 rounded-md border border-border bg-card hover:bg-secondary/60 grid place-items-center text-muted-foreground transition-colors"
                    title="Case files"
                    data-testid="lab-chat-open-files"
                >
                    <FileMagnifyingGlass size={14} />
                </Link>
                <Link
                    to={`/doctor/cases/${caseId}`}
                    className="w-8 h-8 rounded-md border border-border bg-card hover:bg-secondary/60 grid place-items-center text-muted-foreground transition-colors"
                    title="More options"
                    data-testid="lab-chat-more-options"
                >
                    <DotsThreeVertical size={14} weight="bold" />
                </Link>
            </div>
        </div>
    );
}


function ThreadEmpty({ hasInbox, recents = [], onPickCase }) {
    return (
        <div className="flex-1 overflow-y-auto p-6 sm:p-8" data-testid="lab-chat-empty">
            <div className="max-w-lg mx-auto">
                {/* Hero — circular icon + title + sub */}
                <div className="text-center">
                    <div className="inline-flex w-14 h-14 rounded-full bg-secondary items-center justify-center mb-3">
                        <ChatCircleDots size={26} weight="duotone" className="text-muted-foreground" />
                    </div>
                    <h3 className="font-display text-xl font-bold">
                        {hasInbox ? "Pick a case to start" : "No conversations yet"}
                    </h3>
                    <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">
                        {hasInbox
                            ? "Lab Chat is a per-case thread with the COE planning, QA and production teams."
                            : "When you message the lab on a case, it'll show up here. Open a case and use the chat composer to start."}
                    </p>
                </div>

                {/* Recent card — single most-recent case (mirrors PatientChat's
                    "RECENT" affordance). Saves a click vs the left rail when
                    bouncing between cases. */}
                {recents.length > 0 && (
                    <div className="mt-6" data-testid="lab-chat-recent-cases">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-2">Recent</div>
                        <button
                            type="button"
                            onClick={() => onPickCase?.(recents[0].id)}
                            className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl border border-border bg-card hover:bg-secondary/50 hover:border-primary/40 transition text-left"
                            data-testid={`lab-chat-recent-${recents[0].id}`}
                        >
                            <div className="flex items-center gap-3 min-w-0">
                                {recents[0].patient_photo_data_url ? (
                                    <img
                                        src={recents[0].patient_photo_data_url}
                                        alt={recents[0].patient_name || "Patient"}
                                        className="w-9 h-9 rounded-full object-cover border border-border shrink-0"
                                    />
                                ) : (
                                    (() => {
                                        const t = avatarTintFor(recents[0].patient_name);
                                        return (
                                            <div className={`w-9 h-9 rounded-full grid place-items-center text-[11px] font-semibold border ${t.bg} ${t.border} ${t.text} shrink-0`}>
                                                {initialsOf(recents[0].patient_name)}
                                            </div>
                                        );
                                    })()
                                )}
                                <div className="min-w-0">
                                    <div className="font-semibold text-sm truncate">{recents[0].patient_name || "Patient"}</div>
                                    <div className="text-[11px] text-muted-foreground truncate mt-0.5 flex items-center gap-1.5">
                                        <span className="font-mono text-primary">{recents[0].code || "—"}</span>
                                        <span>·</span>
                                        <span className="truncate">{(recents[0].last_message_preview || "—").slice(0, 60)}</span>
                                    </div>
                                </div>
                            </div>
                            <CaretRight size={14} className="text-muted-foreground shrink-0" />
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}


function ThreadBlank() {
    return (
        <div className="text-center text-muted-foreground text-xs py-12">
            <ChatCircleDots size={22} weight="duotone" className="mx-auto mb-2 text-muted-foreground/60" />
            No messages yet — say hello to the lab.
        </div>
    );
}


function MessageBubble({ msg }) {
    // "Mine" = the current doctor / clinic-side message. Everyone else
    // (planner, QA, production, admin) renders left-aligned with their name.
    const mine = msg.author_role === "doctor" || msg.author_role === "clinic_staff";
    const meta = (() => {
        if (msg.author_role === "admin") return { label: "Admin", tone: "bg-violet-50 text-violet-700 border-violet-200" };
        if (msg.author_role === "staff") return { label: msg.author_name, tone: "bg-secondary text-foreground/80 border-border" };
        if (msg.author_role === "clinic_staff") return { label: `${msg.author_name} · Clinic`, tone: "bg-primary/10 text-primary border-primary/30" };
        return { label: msg.author_name, tone: "bg-primary/10 text-primary border-primary/30" };
    })();
    const attachments = msg.attachments || [];
    // Read receipt — only render on the sender's own bubbles. `read_by`
    // includes the sender themselves (server stamps it on insert) plus
    // every recipient who has viewed the thread since. A recipient read
    // means the sender's name is NOT the only entry.
    const recipientReads = (msg.read_by || []).filter((r) => r.user_id !== msg.author_id).length;
    const showCheck = mine && !msg._pending;
    return (
        <div className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`lab-chat-msg-${msg.id}`}>
            <div className="max-w-[78%] min-w-0">
                {!mine && (
                    <div className={`inline-block text-[10px] px-1.5 py-0.5 rounded font-semibold border mb-1 ${meta.tone}`}>
                        {meta.label}
                    </div>
                )}
                <div className={`rounded-2xl px-3.5 py-2 text-sm break-words shadow-sm ${
                    mine
                        ? "bg-primary text-white rounded-br-md"
                        : "bg-secondary/70 text-foreground rounded-bl-md"
                } ${msg._pending ? "opacity-60" : ""}`}>
                    {/* Attachments above text (Slack/WhatsApp convention) */}
                    {attachments.length > 0 && (
                        <div className={`space-y-1.5 ${msg.text ? "mb-2" : ""}`}>
                            {attachments.map((a) => <AttachmentChip key={a.id} att={a} mine={mine} />)}
                        </div>
                    )}
                    {msg.text && <div className="whitespace-pre-wrap">{msg.text}</div>}
                </div>
                <div className={`text-[10px] mt-1 flex items-center gap-1 ${mine ? "justify-end text-muted-foreground" : "text-muted-foreground"}`}>
                    <span>{fmtTime(msg.created_at)}{msg._pending && " · sending…"}</span>
                    {showCheck && (
                        recipientReads > 0 ? (
                            <Checks size={12} weight="bold" className="text-primary" title="Read by the lab" data-testid={`lab-chat-msg-read-${msg.id}`} />
                        ) : (
                            <Check size={12} weight="bold" className="text-muted-foreground/70" title="Sent — not yet read" data-testid={`lab-chat-msg-sent-${msg.id}`} />
                        )
                    )}
                </div>
            </div>
        </div>
    );
}


/* AttachmentChip — inline image preview for images, file card for other
   types. Clickable for download (token-attached via the api helper —
   we use `<a>` with a small loader instead of `<img src>` because the
   files endpoint requires Authorization header). */
function AttachmentChip({ att, mine }) {
    const isImage = (att.content_type || "").startsWith("image/")
        || /\.(png|jpg|jpeg|gif|webp|heic)$/i.test(att.name || "");
    const [blobUrl, setBlobUrl] = useState(null);
    // Lazy-load image blob with the auth header. We don't fetch non-image
    // attachments — those render as a clickable card and the doctor pulls
    // the file from the download endpoint on click.
    useEffect(() => {
        if (!isImage || !att.id) return undefined;
        let revoked = false;
        let url = null;
        api.get(`/files/${att.id}`, { responseType: "blob" })
            .then((r) => {
                if (revoked) return;
                url = URL.createObjectURL(r.data);
                setBlobUrl(url);
            })
            .catch(() => { /* render as a file card on failure */ });
        return () => {
            revoked = true;
            if (url) URL.revokeObjectURL(url);
        };
    }, [att.id, isImage]);

    const downloadFile = async () => {
        try {
            const r = await api.get(`/files/${att.id}`, { responseType: "blob" });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = att.name || "download";
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 1500);
        } catch {
            toast.error("Couldn't download attachment");
        }
    };

    if (isImage) {
        return (
            <button
                type="button"
                onClick={downloadFile}
                className="block max-w-[260px] rounded-lg overflow-hidden border border-black/10 bg-black/5 cursor-zoom-in"
                data-testid={`lab-chat-attachment-${att.id}`}
                title={att.name}
            >
                {blobUrl ? (
                    <img src={blobUrl} alt={att.name} className="block max-h-[200px] w-auto" />
                ) : (
                    <div className="w-[200px] h-[120px] flex items-center justify-center text-xs text-muted-foreground">
                        <Spinner size={16} className="animate-spin" />
                    </div>
                )}
            </button>
        );
    }
    const cardCls = mine
        ? "bg-white/10 hover:bg-white/20 border-white/20 text-white"
        : "bg-card hover:bg-secondary border-border text-foreground";
    return (
        <button
            type="button"
            onClick={downloadFile}
            className={`flex items-center gap-2 max-w-[260px] px-2.5 py-2 rounded-lg border text-left ${cardCls}`}
            data-testid={`lab-chat-attachment-${att.id}`}
        >
            <FileText size={18} weight="duotone" className="shrink-0" />
            <div className="min-w-0 flex-1">
                <div className="text-xs font-semibold truncate">{att.name || "File"}</div>
                <div className={`text-[10px] ${mine ? "text-white/70" : "text-muted-foreground"}`}>
                    {fmtSize(att.size)}
                </div>
            </div>
        </button>
    );
}


function Composer({
    draft, setDraft, onSend, sending,
    pendingAttachments = [], onFilesPicked,
    removePendingAttachment, uploadingCount = 0, fileInputRef,
}) {
    /* Cmd/Ctrl+Enter sends, plain Enter inserts a newline. Matches
       the convention from Slack / Linear and avoids the common
       "I lost my message because I hit Enter" surprise. */
    const onKey = (e) => {
        if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
            e.preventDefault();
            onSend();
        }
    };
    const canSend = !sending && uploadingCount === 0 && (draft.trim().length > 0 || pendingAttachments.length > 0);
    return (
        <div className="border-t border-border p-3 bg-background/50">
            {/* Pending attachments tray — shown above the textarea while
                files are uploaded / queued for send. Removable individually. */}
            {(pendingAttachments.length > 0 || uploadingCount > 0) && (
                <div className="mb-2 flex flex-wrap gap-2" data-testid="lab-chat-attachments-tray">
                    {pendingAttachments.map((a) => (
                        <PendingAttachmentChip
                            key={a.id}
                            att={a}
                            onRemove={() => removePendingAttachment(a.id)}
                        />
                    ))}
                    {uploadingCount > 0 && (
                        <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-secondary/70 border border-border text-[11px] text-muted-foreground">
                            <Spinner size={12} className="animate-spin" />
                            Uploading {uploadingCount} file{uploadingCount === 1 ? "" : "s"}…
                        </div>
                    )}
                </div>
            )}
            <div className="flex items-end gap-2">
                <input
                    type="file"
                    ref={fileInputRef}
                    onChange={(e) => onFilesPicked(e.target.files)}
                    className="hidden"
                    multiple
                    accept="image/*,application/pdf,.stl,.ply,.obj"
                    data-testid="lab-chat-file-input"
                />
                <Button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    variant="outline"
                    size="sm"
                    className="h-10 px-2.5 shrink-0"
                    title="Attach files"
                    data-testid="lab-chat-attach-btn"
                >
                    <Paperclip size={15} />
                </Button>
                <Textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={onKey}
                    placeholder="Type your message to the lab…"
                    rows={2}
                    className="resize-none text-sm"
                    data-testid="lab-chat-composer"
                />
                <Button
                    type="button"
                    onClick={onSend}
                    disabled={!canSend}
                    size="sm"
                    className="h-10 shrink-0"
                    data-testid="lab-chat-send"
                >
                    {sending ? <Spinner size={14} className="animate-spin" /> : <PaperPlaneTilt size={14} weight="fill" />}
                    <span className="ml-1.5 hidden sm:inline">Send</span>
                </Button>
            </div>
            <div className="text-[10px] text-muted-foreground mt-1.5">
                Press Cmd / Ctrl + Enter to send. Attach images, PDFs, or STL files.
            </div>
        </div>
    );
}


/* Tray chip — small preview tile in the composer while the attachment
   is waiting to be sent. Image preview if image type, otherwise file
   icon + name. The × button removes it from the tray (the file remains
   on the server as a chat_attachment that hasn't been referenced — we
   accept the small dangling-file cost in exchange for a snappier UX). */
function PendingAttachmentChip({ att, onRemove }) {
    return (
        <div className="relative inline-flex items-center gap-1.5 pl-2 pr-1 py-1 rounded-md bg-secondary border border-border text-[11px]" data-testid={`lab-chat-tray-${att.id}`}>
            {att.is_image
                ? <ImageIcon size={13} weight="duotone" className="text-primary shrink-0" />
                : <FileText size={13} weight="duotone" className="text-muted-foreground shrink-0" />}
            <span className="font-medium truncate max-w-[140px]">{att.name}</span>
            <button
                type="button"
                onClick={onRemove}
                className="w-5 h-5 rounded-full hover:bg-rose-100 hover:text-rose-700 flex items-center justify-center transition-colors"
                title="Remove"
                data-testid={`lab-chat-tray-remove-${att.id}`}
            >
                <X size={11} weight="bold" />
            </button>
        </div>
    );
}
